import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { PLANS, getPlanPriceDisplay } from '../config/plans';
import { CreditCard, Clock, CheckCircle2, AlertTriangle, XCircle, Loader2, RefreshCw, X } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const getDaysRemaining = (trialEndsAt) => {
  if (!trialEndsAt) return null;
  return Math.max(0, Math.ceil((new Date(trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)));
};

const StatusBadge = ({ status }) => {
  const { t } = useTranslation('public');
  const config = {
    trialing:  { icon: Clock,          className: 'bg-blue-100 text-blue-700' },
    active:    { icon: CheckCircle2,   className: 'bg-green-100 text-green-700' },
    past_due:  { icon: AlertTriangle,  className: 'bg-red-100 text-red-700' },
    suspended: { icon: XCircle,        className: 'bg-gray-100 text-gray-700' },
  };
  const { icon: Icon, className } = config[status] || config.past_due;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${className}`}>
      <Icon className="h-4 w-4" />
      {t(`billing.statuses.${status}`, t('billing.statuses.past_due'))}
    </span>
  );
};

export default function Billing() {
  const { t, i18n } = useTranslation('public');
  const { user, refreshSubscription } = useAuth();
  const subscription = user?.subscription;
  const currentPlanId = subscription?.planId || 'pro';
  const [paytrToken, setPaytrToken] = useState(null);
  const [paytrLoading, setPaytrLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [billingCycle, setBillingCycle] = useState(subscription?.billingCycle || 'monthly');
  const didHandleUrlParam = useRef(false);

  const daysRemaining = getDaysRemaining(subscription?.trialEndsAt);

  // ?payment=success/failed URL param — sayfa reload olursa da çalışır
  useEffect(() => {
    if (didHandleUrlParam.current) return;
    const params = new URLSearchParams(window.location.search);
    const paymentResult = params.get('payment');
    if (paymentResult === 'success') {
      didHandleUrlParam.current = true;
      window.history.replaceState({}, '', '/billing');
      refreshSubscription().then(() => {
        toast.success(t('billing.toasts.paymentSuccess'));
      });
    } else if (paymentResult === 'failed') {
      didHandleUrlParam.current = true;
      window.history.replaceState({}, '', '/billing');
      toast.error(t('billing.toasts.paymentFailed'));
    }
  }, [refreshSubscription]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshSubscription();
    setRefreshing(false);
    toast.success(t('billing.toasts.refreshed'));
  };

  const handlePayment = async (planId, cycle) => {
    const initUrl = process.env.REACT_APP_PAYTR_INIT_URL;
    if (!initUrl) {
      toast.error(t('billing.toasts.paymentNotConfigured'));
      return;
    }
    setPaytrLoading(true);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(initUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ planId, billingCycle: cycle }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Token alınamadı');
      setPaytrToken(data.token);
    } catch (err) {
      toast.error(t('billing.toasts.paymentError', { error: err.message }));
    } finally {
      setPaytrLoading(false);
    }
  };

  // PayTR iframe mesajını dinle (ödeme tamamlandığında)
  useEffect(() => {
    const handleMessage = async (e) => {
      const allowed = ['https://www.paytr.com', window.location.origin];
      if (!allowed.includes(e.origin)) return;
      if (e.data === 'success') {
        setPaytrToken(null);
        await refreshSubscription();
        toast.success(t('billing.toasts.paymentSuccess'));
      } else if (e.data === 'failed') {
        setPaytrToken(null);
        toast.error(t('billing.toasts.paymentFailed'));
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [refreshSubscription]);

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* PayTR Modal */}
      {paytrToken && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setPaytrToken(null)}
          />
          {/* Modal kutu */}
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 flex flex-col" style={{ maxHeight: '90vh' }}>
            {/* Header — sabit */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-blue-600" />
                <span className="font-semibold text-gray-800 text-sm">{t('billing.paymentModal.title')}</span>
              </div>
              <button
                onClick={() => setPaytrToken(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {/* iframe wrapper — header ~64px çıkartılmış hesaplı yükseklik */}
            <div style={{ height: 'calc(90vh - 64px)', overflow: 'hidden' }}>
              <iframe
                src={`https://www.paytr.com/odeme/guvenli/${paytrToken}`}
                title="PayTR Ödeme"
                style={{ width: '100%', height: '100%', display: 'block' }}
                frameBorder="0"
                scrolling="yes"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('billing.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('billing.subtitle')}</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {t('billing.refresh')}
        </button>
      </div>

      {/* Mevcut Durum */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-800 mb-2">{t('billing.statusSection.title')}</h2>
            {subscription ? (
              <StatusBadge status={subscription.status} />
            ) : (
              <span className="text-sm text-gray-500">{t('billing.statusSection.loading')}</span>
            )}
          </div>
          {subscription?.status === 'active' && (
            <div className="text-right">
              <p className="text-xs text-gray-500">{t('billing.statusSection.nextPayment')}</p>
              <p className="text-sm font-medium text-gray-800">
                {subscription.currentPeriodEnd
                  ? new Date(subscription.currentPeriodEnd).toLocaleDateString('tr-TR')
                  : '—'}
              </p>
            </div>
          )}
        </div>

        {/* Trial countdown */}
        {subscription?.status === 'trialing' && daysRemaining !== null && (
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">{t('billing.trial.label')}</span>
              <span className={`font-semibold ${daysRemaining <= 3 ? 'text-red-600' : daysRemaining <= 10 ? 'text-yellow-600' : 'text-blue-600'}`}>
                {t('billing.trial.daysLeft', { count: daysRemaining })}
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${daysRemaining <= 3 ? 'bg-red-500' : daysRemaining <= 10 ? 'bg-yellow-500' : 'bg-blue-500'}`}
                style={{ width: `${Math.min(100, (daysRemaining / 30) * 100)}%` }}
              />
            </div>
            {subscription.trialEndsAt && (
              <p className="text-xs text-gray-400 mt-1">
                {t('billing.trial.endsOn', { date: new Date(subscription.trialEndsAt).toLocaleDateString('tr-TR') })}
              </p>
            )}
          </div>
        )}

        {/* Past due uyarısı */}
        {(subscription?.status === 'past_due' || subscription?.status === 'suspended') && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">
              {t('billing.pastDue.message')}
            </p>
          </div>
        )}
      </div>

      {/* Aylık / Yıllık Toggle */}
      <div className="flex items-center justify-center gap-3 mb-6">
        <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-400'}`}>
          {t('billing.monthly')}
        </span>
        <button
          type="button"
          onClick={() => setBillingCycle((prev) => prev === 'monthly' ? 'yearly' : 'monthly')}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${billingCycle === 'yearly' ? 'bg-blue-600' : 'bg-gray-300'}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
        <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-400'}`}>
          {t('billing.yearly')}
          <span className={`ml-1.5 text-xs font-semibold px-1.5 py-0.5 rounded-full ${billingCycle === 'yearly' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
            {t('billing.savingBadge')}
          </span>
        </span>
      </div>

      {/* 3 Plan Kartı */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {PLANS.map((plan) => {
          const priceDisplay = getPlanPriceDisplay(plan.id, billingCycle, i18n.language);
          const isCurrentPlan = plan.id === currentPlanId;
          const isCurrentAndActive = isCurrentPlan && subscription?.status === 'active';
          const isCurrentTrialing = isCurrentPlan && subscription?.status === 'trialing';

          return (
            <div
              key={plan.id}
              className={`relative rounded-xl border-2 p-5 flex flex-col ${
                isCurrentAndActive
                  ? 'border-green-400 bg-green-50'
                  : isCurrentTrialing
                  ? 'border-blue-500 bg-blue-50'
                  : plan.popular
                  ? 'border-blue-200 bg-white'
                  : 'border-gray-200 bg-white'
              }`}
            >
              {isCurrentAndActive && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white text-[11px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap">
                  {t('billing.activePlanBadge')}
                </span>
              )}
              {isCurrentTrialing && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[11px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap">
                  {t('billing.trialPlanBadge')}
                </span>
              )}
              {!isCurrentAndActive && !isCurrentTrialing && plan.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[11px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap">
                  {t('billing.mostPreferred')}
                </span>
              )}

              <div className="mb-3">
                <h3 className="font-bold text-gray-900 text-base">{t(`plans.${plan.id}.name`)}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{t(`plans.${plan.id}.description`)}</p>
              </div>

              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-2xl font-bold text-gray-900">
                  {priceDisplay.symbolBefore ? `${priceDisplay.symbol}${priceDisplay.formatted}` : priceDisplay.formatted}
                </span>
                {!priceDisplay.symbolBefore && (
                  <span className="text-gray-500 text-sm">{priceDisplay.symbol}</span>
                )}
                <span className="text-gray-400 text-xs">/{billingCycle === 'yearly' ? t('billing.perYear') : t('billing.perMonth')}</span>
              </div>
              {billingCycle === 'yearly' && (
                <p className="text-xs text-green-600 mb-3">
                  {t('billing.approxMonthly', {
                    amount: priceDisplay.symbolBefore
                      ? `${priceDisplay.symbol}${priceDisplay.yearlyMonthlyEquivFormatted}`
                      : `${priceDisplay.yearlyMonthlyEquivFormatted} ${priceDisplay.symbol}`,
                  })}
                </p>
              )}

              <ul className="space-y-1.5 mb-5 flex-1 mt-2">
                {(t(`plans.${plan.id}.highlights`, { returnObjects: true }) || []).map((h) => (
                  <li key={h} className="flex items-start gap-1.5 text-xs text-gray-600">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                    {h}
                  </li>
                ))}
              </ul>

              {isCurrentAndActive ? (
                <div className="w-full text-center py-2 rounded-lg bg-green-100 text-green-700 font-medium text-sm">
                  {t('billing.currentPlan')}
                </div>
              ) : (
                <button
                  onClick={() => handlePayment(plan.id, billingCycle)}
                  disabled={paytrLoading}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-semibold py-2 rounded-lg transition-colors text-sm"
                >
                  {paytrLoading ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> {t('billing.preparing')}</>
                  ) : (
                    <>
                      <CreditCard className="h-3.5 w-3.5" />
                      {subscription?.status === 'active' ? t('billing.switchPlan') : t('billing.startPlan')}
                    </>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-400 text-center mt-4">
        {t('billing.supportText')} <a href={`mailto:${t('billing.supportEmail')}`} className="underline">{t('billing.supportEmail')}</a>
      </p>
    </div>
  );
}
